import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";
import { useUnreadMessages } from "@/hooks/useUnreadMessages";
import { supabase } from "@/integrations/supabase/client";
import { NotificationBell } from "@/components/NotificationBell";
import { BrandWordmark } from "@/components/BrandWordmark";
import { BrandMark } from "@/components/BrandMark";
import {
  Trophy, LogOut, Users, User,
  Medal, BarChart3, Swords, Target, LayoutDashboard,
  TrendingUp, ListChecks, Crown, Sparkles,
} from "lucide-react";
import { useSubscription } from "@/hooks/useSubscription";
import { NavLink } from "@/components/NavLink";
import {
  Sidebar,
  SidebarContent,
  SidebarGroup,
  SidebarGroupLabel,
  SidebarGroupContent,
  SidebarMenu,
  SidebarMenuItem,
  SidebarMenuButton,
  SidebarFooter,
  SidebarHeader,
  SidebarSeparator,
  useSidebar,
} from "@/components/ui/sidebar";
import { motion } from "framer-motion";

const mainNav = [
  { to: "/", label: "Clubhouse", icon: LayoutDashboard, exact: true },
  { to: "/my-brackets", label: "My Brackets", icon: ListChecks },
  { to: "/tournaments", label: "Tournaments", icon: Trophy },
  { to: "/leaderboard", label: "Leaderboard", icon: Medal },
  { to: "/groups", label: "Groups", icon: Users, badge: true },
];

const insightsNav = [
  { to: "/analytics", label: "Analytics", icon: BarChart3 },
  { to: "/compare", label: "Head-to-Head", icon: Swords },
  { to: "/stats", label: "My Stats", icon: Target },
];

export function AppSidebar() {
  const { user, signOut } = useAuth();
  const { unreadCount } = useUnreadMessages();
  const { hasSeasonPass, activeCount } = useSubscription();
  const { state, isMobile, setOpenMobile } = useSidebar();
  const collapsed = state === "collapsed";

  // On mobile the sidebar is an overlay drawer. When a user taps any
  // nav item / CTA, close it immediately so the destination page is
  // visible without requiring a second tap to dismiss the drawer.
  const closeOnMobile = () => { if (isMobile) setOpenMobile(false); };
  const location = useLocation();
  const [profile, setProfile] = useState<{ display_name: string; avatar_url: string | null } | null>(null);
  useEffect(() => {
    if (!user) return;
    supabase
      .from("profiles")
      .select("display_name, avatar_url")
      .eq("id", user.id)
      .single()
      .then(({ data }) => { if (data) setProfile(data); });
  }, [user]);

  const isActive = (path: string, exact?: boolean) =>
    exact ? location.pathname === path : location.pathname === path;

  const renderNavItem = (item: { to: string; label: string; icon: any; exact?: boolean; badge?: boolean }) => (
    <SidebarMenuItem key={item.to}>
      <SidebarMenuButton asChild isActive={isActive(item.to, item.exact)}>
        <NavLink
          to={item.to}
          end={item.exact}
          onClick={closeOnMobile}
          className="flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-body font-medium"
          activeClassName="relative bg-gradient-to-r from-primary/20 via-primary/5 to-transparent text-foreground font-semibold before:absolute before:left-0 before:top-1 before:bottom-1 before:w-[3px] before:bg-primary"
        >
          <item.icon className="w-4 h-4 shrink-0" />
          {!collapsed && <span>{item.label}</span>}
          {item.badge && unreadCount > 0 && !collapsed && (
            <motion.span
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              className="ml-auto min-w-[18px] h-[18px] flex items-center justify-center rounded-full bg-destructive text-destructive-foreground text-[10px] font-bold px-1"
            >
              {unreadCount > 99 ? "99+" : unreadCount}
            </motion.span>
          )}
        </NavLink>
      </SidebarMenuButton>
    </SidebarMenuItem>
  );

  return (
    <Sidebar collapsible="icon">
      <SidebarHeader className="p-4 pb-2">
        <Link to="/" onClick={closeOnMobile} className="flex items-center justify-center py-2">
          {collapsed ? (
            <BrandMark size={32} className="text-primary shrink-0" />
          ) : (
            <BrandWordmark size="md" />
          )}
        </Link>
      </SidebarHeader>

      <SidebarContent>
        <SidebarGroup>
          <SidebarGroupLabel>Main</SidebarGroupLabel>
          <SidebarGroupContent>
            <SidebarMenu>{mainNav.map(renderNavItem)}</SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>

        <SidebarSeparator />

        <SidebarGroup>
          <SidebarGroupLabel>Insights</SidebarGroupLabel>
          <SidebarGroupContent>
            <SidebarMenu>{insightsNav.map(renderNavItem)}</SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>

        <SidebarSeparator />

        <SidebarGroup>
          <SidebarGroupLabel>More</SidebarGroupLabel>
          <SidebarGroupContent>
            <SidebarMenu>
              {renderNavItem({ to: "/affiliate", label: "Affiliate", icon: TrendingUp })}
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>
      </SidebarContent>

      <SidebarFooter className="p-3 space-y-2">
        {/* Paid upgrade CTA — only for free users. Expanded-only (tiny
            crown icon in rail mode keeps collapsed sidebar calm). */}
        {!hasSeasonPass && (
          collapsed ? (
            <Link
              to="/pricing"
              onClick={closeOnMobile}
              title={activeCount > 0 ? "Upgrade to Season Pass" : "Unlock prize pools"}
              className="flex items-center justify-center w-10 h-10 mx-auto rounded-lg bg-primary/15 border border-primary/30 text-primary hover:bg-primary/25 transition-colors"
            >
              <Crown className="w-5 h-5" />
            </Link>
          ) : activeCount > 0 ? (
            // User already has a single-tournament sub — pitch the $19.99
            // upgrade instead of the full $29.99 Season Pass price. Route
            // through /checkout-success where the upgrade CTA lives + is
            // wired to the season_pass_upgrade priceType.
            <Link
              to="/checkout-success?type=single_tournament&offer=upgrade"
              onClick={closeOnMobile}
              className="block rounded-xl p-3 bg-gradient-to-br from-primary/20 via-primary/10 to-transparent border border-primary/30 hover:border-primary/50 transition-colors group"
            >
              <div className="flex items-center gap-2 mb-1">
                <Crown className="w-4 h-4 text-primary" />
                <span className="font-display text-xs font-bold uppercase tracking-wider text-primary">
                  Go All-In
                </span>
              </div>
              <p className="text-xs font-semibold text-sidebar-foreground leading-tight mb-1">
                Unlock every tournament
              </p>
              <p className="text-[10px] text-sidebar-foreground/60 leading-tight mb-2">
                <Sparkles className="w-2.5 h-2.5 inline mr-0.5 text-primary" />
                One-time upgrade · only $19.99 more
              </p>
              <span className="block text-center text-[11px] font-display font-bold text-primary group-hover:underline">
                Upgrade →
              </span>
            </Link>
          ) : (
            <Link
              to="/pricing"
              onClick={closeOnMobile}
              className="block rounded-xl p-3 bg-gradient-to-br from-primary/20 via-primary/10 to-transparent border border-primary/30 hover:border-primary/50 transition-colors group"
            >
              <div className="flex items-center gap-2 mb-1">
                <Crown className="w-4 h-4 text-primary" />
                <span className="font-display text-xs font-bold uppercase tracking-wider text-primary">
                  Go Pro
                </span>
              </div>
              <p className="text-xs font-semibold text-sidebar-foreground leading-tight mb-1">
                Play for real prizes
              </p>
              <p className="text-[10px] text-sidebar-foreground/60 leading-tight mb-2">
                <Sparkles className="w-2.5 h-2.5 inline mr-0.5 text-primary" />
                From $9.99/mo · all 9 for $29.99/mo
              </p>
              <span className="block text-center text-[11px] font-display font-bold text-primary group-hover:underline">
                View Plans →
              </span>
            </Link>
          )
        )}

        <SidebarSeparator />

        {/* Profile */}
        {user && (
          <div className="space-y-1">
            <Link
              to="/profile"
              onClick={closeOnMobile}
              className="flex items-center gap-3 px-3 py-2 rounded-lg hover:bg-sidebar-accent transition-colors"
            >
              <div className="w-7 h-7 rounded-full bg-sidebar-primary/20 border-2 border-sidebar-primary/40 flex items-center justify-center overflow-hidden shrink-0">
                {profile?.avatar_url ? (
                  <span className="text-sm leading-none">{profile.avatar_url}</span>
                ) : (
                  <User className="w-3.5 h-3.5 text-sidebar-primary" />
                )}
              </div>
              {!collapsed && (
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-semibold text-sidebar-foreground truncate">
                    {profile?.display_name || user.email?.split("@")[0] || "Player"}
                  </p>
                  <p className="text-xs text-sidebar-foreground/50 truncate">{user.email}</p>
                </div>
              )}
            </Link>
            <button
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                signOut();
              }}
              className="flex items-center gap-3 w-full px-3 py-2 rounded-lg text-sm font-body text-destructive hover:bg-destructive/10 transition-colors"
            >
              <LogOut className="w-4 h-4 shrink-0" />
              {!collapsed && <span>Sign Out</span>}
            </button>
          </div>
        )}

        {/* Footer links */}
        {!collapsed && (
          <div className="flex items-center justify-center gap-3 pt-2 text-[10px] text-sidebar-foreground/40">
            <Link to="/privacy" onClick={closeOnMobile} className="hover:text-sidebar-foreground/60 transition-colors">Privacy</Link>
            <span>·</span>
            <Link to="/terms" onClick={closeOnMobile} className="hover:text-sidebar-foreground/60 transition-colors">Terms</Link>
            <span>·</span>
            <Link to="/contact" onClick={closeOnMobile} className="hover:text-sidebar-foreground/60 transition-colors">Contact</Link>
          </div>
        )}
        {!collapsed && (
          <p className="text-[10px] text-sidebar-foreground/30 text-center pt-1">
            © 2026 FCbracket.com
          </p>
        )}
      </SidebarFooter>
    </Sidebar>
  );
}
